import { useState } from "react";

export default function Toggle({
  name,
  falseLabel,
  falseValue,
  trueLabel,
  trueValue,
  defaultValue,
  onChange,
  className,
}: {
  name: string;
  falseLabel: string;
  falseValue: string | number | boolean;
  trueLabel: string;
  trueValue: string | number | boolean;
  defaultValue: string | number | boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
}) {
  const [checked, setChecked] = useState(defaultValue === trueValue);

  return (
    <div
      className={`flex items-start float-left gap-2 w-full h-full  ${className}`}
    >
      <label className={`inline-flex items-center cursor-pointer min-w-0 `}>
        <input
          name={name}
          type="checkbox"
          className="sr-only peer"
          checked={checked}
          onChange={(e) => {
            setChecked(e.target.checked);
            onChange?.(e);
          }}
        />
        <div
          className={`relative w-11 h-6 ${
            checked ? "bgPrimaryGreen" : "bg-White"
          }
         peer-focus:outline-none peer-focus:ring-2 
         ring-2 ring-SkaneGreen dark:peer-focus:ring-SkaneGreen rounded-full 
         peer-focus:ring-SkaneGreen
         peer-checked:bg-SkaneGreen peer-checked:ring-White peer-checked:peer-focus:ring-White peer-checked:
         peer dark:bg-SkaneGreen peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full
         after:shadow-lg after:shadow-SkaneGreen
          peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-White after:border-Grey4 after:border after:rounded-full after:h-5 after:w-5 after:transition-all
           dark:border-gray-600 peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600 flex-shrink-0`}
        ></div>
        <span className="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300 ">
          {checked ? trueLabel : falseLabel}
        </span>
      </label>
    </div>
  );
}

export function ToggleCss({
  name,
  falseLabel,
  falseValue,
  trueValue,
  trueLabel,
  defaultValue,
  onChange,
  className,
  size = 24,
}: {
  name: string;
  falseLabel: string;
  falseValue: string | number | boolean;
  trueLabel: string;
  trueValue: string | number | boolean;
  defaultValue: string | number | boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
  size?: number;
}) {
  const isChecked = defaultValue === trueValue;
  const borderWidth = 2;
  const toggleWidth = size * 1.83;
  const toggleHeight = size;
  const thumbSize = size * 0.83;
  // Center the thumb vertically (accounting for border)
  const thumbOffsetY = (toggleHeight - thumbSize) / 2;
  // Left position accounting for border
  const thumbOffsetX = borderWidth + (toggleHeight - thumbSize) / 2;
  // Translation distance: move from left to right, accounting for borders on both sides
  const translateDistance = toggleWidth - thumbSize - thumbOffsetX * 2;
  const uniqueId = `toggle-css-${name}-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div
      className={`flex items-start float-left gap-2 w-full h-full ${className || ""}`}
    >
      <label className="inline-flex items-center cursor-pointer min-w-0 relative">
        <input
          name={name}
          type="checkbox"
          className="sr-only"
          defaultChecked={isChecked}
          onChange={onChange}
          id={uniqueId}
        />
        <div
          className={`toggle-css-track-${uniqueId}`}
          style={{
            width: `${toggleWidth}px`,
            height: `${toggleHeight}px`,
          }}
        />
        <span className="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300">
          <span className={`toggle-label-false-${uniqueId}`}>{falseLabel}</span>
          <span className={`toggle-label-true-${uniqueId}`}>{trueLabel}</span>
        </span>
        <style>{`
          .toggle-css-track-${uniqueId} {
            position: relative;
            border-radius: 9999px;
            border: 2px solid #22c55e;
            background-color: #ffffff;
            transition: all 0.2s ease-in-out;
            flex-shrink: 0;
          }
          
          #${uniqueId}:checked ~ .toggle-css-track-${uniqueId} {
            background-color: #22c55e;
            border-color: #22c55e;
          }
          
          .toggle-css-track-${uniqueId}::after {
            content: '';
            position: absolute;
            width: ${thumbSize}px;
            height: ${thumbSize}px;
            top: ${thumbOffsetY}px;
            left: ${thumbOffsetX}px;
            background-color: #ffffff;
            border: 1px solid #d1d5db;
            border-radius: 50%;
            transition: transform 0.2s ease-in-out;
            transform: translateX(0);
          }
          
          #${uniqueId}:checked ~ .toggle-css-track-${uniqueId}::after {
            transform: translateX(${translateDistance}px);
          }
          
          #${uniqueId}:focus ~ .toggle-css-track-${uniqueId} {
            outline: 2px solid #22c55e;
            outline-offset: 2px;
          }
          
          .toggle-label-true-${uniqueId} {
            display: none;
          }
          
          #${uniqueId}:checked ~ span .toggle-label-false-${uniqueId} {
            display: none;
          }
          
          #${uniqueId}:checked ~ span .toggle-label-true-${uniqueId} {
            display: inline;
          }
        `}</style>
      </label>
    </div>
  );
}